import React, { useState, useEffect, useId, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  LinearProgress,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import addIcone from '@assets/Analytics/add.svg'
import Group2 from '@assets/Analytics/Group2.svg'
import SearchBar from '@/components/common/SearchBar'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import add2 from '@assets/Analytics/add2.svg'
import X from '@assets/Analytics/X.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  searchWaterTreeConsumers,
  WaterTreeConsumerSearchResult,
} from '@/services/dashboardService'
import {
  DEFAULT_TREE_MEMBER_ROLE,
  type TreeMemberRole,
} from '@/services/waterTreeMemberRoles'
import MemberRolePicker from './MemberRolePicker'
import { useSearch } from '@/hooks/useSearch'

export interface DrawerContentProps {
  onClose: () => void
  rows?: any[]
}

type Props = {
  width?: number | string
  height?: number
  title?: string
  drawerContent?: React.ComponentType<DrawerContentProps>
  onOpenFullTable?: () => void
  onAdd?: (premiseIds: number[], role: TreeMemberRole) => void | Promise<void>
  onClear?: () => void
  zoneId?: number | string
  zoneType?: 1 | 2
  accountId?: string
  onSelectionChange?: (ids: number[]) => void
  consumerRows?: any[]
}

const AddConsumerPanel: React.FC<Props> = ({
  width = 460,
  title = 'הוספת צרכן',
  drawerContent,
  onOpenFullTable,
  onAdd,
  onClear,
  zoneId,
  zoneType = 2,
  accountId,
  consumerRows = [],
}) => {
  const [openDrawer, setOpenDrawer] = useState(false)
  const search = useSearch()
  const [results, setResults] = useState<WaterTreeConsumerSearchResult[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [adding, setAdding] = useState(false)
  const [role, setRole] = useState<TreeMemberRole>(DEFAULT_TREE_MEMBER_ROLE)
  const titleId = useId()
  const DrawerContent = drawerContent
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // An empty text goes through the same path, so it also stops a request that is still running.
  const { run, query } = search
  useEffect(() => {
    run(
      signal =>
        query && zoneId && accountId
          ? searchWaterTreeConsumers(
              accountId,
              zoneId,
              query,
              20,
              zoneType,
              signal
            )
          : Promise.resolve([]),
      setResults,
      err => console.error('[AddConsumerPanel] search:', err)
    )
  }, [query, zoneId, zoneType, accountId, run])

  const visibleResults = useMemo(() => {
    const existingPremiseIds = new Set(
      consumerRows
        .map(row => Number(row.premiseId ?? row.premise_id ?? row.id))
        .filter(Number.isFinite)
    )
    return results.filter(row => !existingPremiseIds.has(row.premise_id))
  }, [results, consumerRows])
  const loading = search.isSearching
  // A cleared box shows nothing, whatever answer is still on its way or on the page.
  const showResults =
    search.text.trim() !== '' && (loading || visibleResults.length > 0)

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAdd = async () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    setAdding(true)
    try {
      await onAdd?.(ids, role)
      setSelected(new Set())
      search.searchNow('')
      setResults([])
    } finally {
      setAdding(false)
    }
  }

  const handleClear = () => {
    setSelected(new Set())
    search.searchNow('')
    setResults([])
    onClear?.()
  }

  const bgColor = isDarkMode ? '#202B40' : '#FAFBFD'
  const textColor = isDarkMode ? '#BBD7FF' : '#003F61'
  const borderColor = isDarkMode ? '#43597F' : 'rgba(0,0,0,0.08)'

  return (
    <>
      <Box
        component="section"
        aria-labelledby={titleId}
        sx={{
          border: isDarkMode ? `1.5px solid ${borderColor}` : `1px solid ${borderColor}`,
          borderRadius: '12px',
          backgroundColor: bgColor,
          width,
          display: 'flex',
          flexDirection: 'column',
          px: 3,
          py: 2,
          boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.3)' : 'none',
          direction: 'ltr',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Button
            onClick={() => { setOpenDrawer(true); onOpenFullTable?.() }}
            sx={{
              backgroundColor: isDarkMode ? '#1D325A' : '#E3F5FF',
              borderRadius: '8px',
              px: 2,
              py: 0.6,
              minWidth: 'unset',
              textTransform: 'none',
              border: isDarkMode ? '1px solid rgba(114, 164, 235, 0.2)' : '1px solid #72A4EB',
              '&:hover': { backgroundColor: isDarkMode ? '#263F6E' : '#CBE0EF' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 900, color: isDarkMode ? '#BBD7FF' : '#003F61', fontFamily: 'Rubik' }}>
                לטבלה המלאה
              </Typography>
              <Box
                component="img"
                src={Group2}
                sx={{
                  width: 13,
                  height: 13,
                  filter: 'invert(87%) sepia(35%) saturate(760%) hue-rotate(185deg) brightness(101%) contrast(105%)',
                }}
              />
            </Box>
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Typography id={titleId} sx={{ fontSize: '18px', fontWeight: 900, color: textColor, fontFamily: 'Rubik' }}>
              {title}
            </Typography>
            <Box component="img" src={addIcone} sx={{ width: 22, height: 22, filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }} />
          </Box>
        </Box>

        <Box sx={{ width: '100%', mb: 1.5 }}>
          <SearchBar
            onSearch={search.setText}
            executeSearch={search.searchNow}
            width="100%"
            height={40}
            placeholder="הקלד שם, כתובת, מספר מד או כרטיס מים"
            sx={{
              bgcolor: isDarkMode ? '#091327 !important' : '#FFFFFF !important',
              border: isDarkMode ? '1.5px solid #43597F' : '1.2px solid #EAEDF2',
              '& .MuiInputBase-root': { 
                fontSize: '13px', 
                direction: 'rtl',
                color: isDarkMode ? '#FFFFFF' : '#003F61',
              },
              '& > span, & > div:first-of-type': { 
                color: isDarkMode ? '#FFFFFF' : '#003F61',
              }
            }}
          />
        </Box>

        {showResults && (
          <Box
            sx={{
              maxHeight: 220,
              overflowY: 'auto',
              border: `1px solid ${borderColor}`,
              borderRadius: '6px',
              mb: 1.5,
              bgcolor: isDarkMode ? '#091327' : '#FFFFFF',
              direction: 'rtl',
            }}
          >
            {loading && (
              <Box sx={{ py: 2, px: 1.2 }}>
                <LinearProgress />
              </Box>
            )}
            {!loading && visibleResults.map(r => (
              <Box
                key={r.premise_id}
                onClick={() => toggleSelect(r.premise_id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.2,
                  py: 0.8,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${borderColor}`,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.05)' : '#F1F8FE' },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected.has(r.premise_id)}
                  sx={{ p: 0.3, color: textColor, '&.Mui-checked': { color: '#72A4EB' } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: textColor, fontFamily: 'Rubik' }}>
                    {r.payer_name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: isDarkMode ? '#94A3B8' : '#6B7280', fontFamily: 'Rubik' }}>
                    {r.address} · מד {r.meter_number} · כרטיס {r.billing_id}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ mb: 1.5 }}>
          <MemberRolePicker value={role} onChange={setRole} disabled={adding} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={selected.size === 0 || adding}
            onClick={handleAdd}
            sx={{
              backgroundColor: '#004A7C',
              color: '#FFFFFF',
              borderRadius: '6px',
              px: 4,
              height: 36,
              fontSize: '14px',
              fontWeight: 900,
              textTransform: 'none',
              fontFamily: 'Rubik',
              '&:hover': { backgroundColor: '#003559' },
              '&.Mui-disabled': { opacity: 0.5, color: '#FFFFFF' },
            }}
          >
            {adding ? 'מוסיף…' : `הוספה${selected.size > 0 ? ` (${selected.size})` : ''}`}
          </Button>

          <Typography
            onClick={handleClear}
            sx={{
              fontSize: '14px',
              fontWeight: 900,
              color: isDarkMode ? '#94A3B8' : '#003F61',
              cursor: 'pointer',
              fontFamily: 'Rubik',
              '&:hover': { color: isDarkMode ? '#BBD7FF' : '#72A4EB' },
            }}
          >
            נקה הכל
          </Typography>
        </Box>
      </Box>

      <SwipeableDrawer
        anchor="right"
        open={openDrawer}
        onOpen={() => setOpenDrawer(true)}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', md: '94vw', lg: '92vw' },
            maxWidth: '1750px',
            bgcolor: isDarkMode ? '#091327' : '#FAFBFD',
          },
        }}
      >
        <Box sx={{ p: '24px 32px', direction: 'rtl' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box component="img" src={add2} sx={{ width: 24, height: 24, filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }} />
              <Typography sx={{ color: textColor, fontWeight: 900, fontSize: '22px', fontFamily: 'Rubik' }}>{title}</Typography>
            </Box>
            <IconButton onClick={() => setOpenDrawer(false)} sx={{ color: isDarkMode ? '#FFFFFF' : '#003F61' }}>
              <Box component="img" src={X} sx={{ width: 20, height: 20, filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#E6ECF2' }} />
          {DrawerContent ? <DrawerContent onClose={() => setOpenDrawer(false)} rows={consumerRows} /> : null}
        </Box>
      </SwipeableDrawer>
    </>
  )
}

export default AddConsumerPanel
